import React from 'react';
import { Query } from 'react-apollo';
import { curry, get, pick, reduce, isFunction } from 'lodash';

import buildQueryForField from './tireOptionsQuery';
import { buildTireOptions } from '../../../CaseRequestsPanelContext/utils';
import { seeNotesRequestValue } from '../constants';

const filterTypes = {
  axleType: 'axleType',
  tireSize: 'tireSizeId',
  productType: 'productTypeId',
  manufacturerFullName: 'manufacturerId',
  sculptureTreadName: 'sculptureTreadId',
};

const selectTypes = {
  axleType: 'AXLE_TYPE',
  tireSize: 'TIRE_SIZE',
  loadRange: 'LOAD_RANGE',
  productType: 'PRODUCT_TYPE',
  tirePosition: 'POSITION',
  sculptureTreadName: 'SCULPTURE_TREAD',
  manufacturerFullName: 'MANUFACTURER',
};

const areCellOptionsLoadedAsync = (name, props) =>
  Array.isArray(getCellConfig(name, props).dependsOn);

const getCellConfig = (name, props) => {
  const { columnsConfig } = props;

  const columnConfig = columnsConfig.find(c => c.name === name) || {};
  const runTimeConfig = isFunction(columnConfig.getRuntimeConfig)
    ? columnConfig.getRuntimeConfig({ ...props, name })
    : {};

  return { ...columnConfig, ...runTimeConfig };
};

const getOptionId = ({ type, value, rowData, tireOptionsByType }) => {
  const lineId = get(rowData, 'id');
  const options = get(tireOptionsByType, `${lineId}.${type}`, []);
  const option = options.find(o => [o.id, o.title].includes(value));
  const upstreamId = get(option, 'upstreamId');

  return upstreamId !== undefined ? upstreamId : get(option, 'id');
};

export const buildVariables = props => {
  const { name, rowData, tireOptionsByType } = props;

  const filters = reduce(
    pick(rowData, getCellConfig(name, props).dependsOn),
    (acc, value, type) => {
      if (!filterTypes[type]) return acc;

      const id = areCellOptionsLoadedAsync(type, props)
        ? getOptionId({ type, value, rowData, tireOptionsByType })
        : value;

      return { ...acc, [filterTypes[type]]: id };
    },
    {},
  );

  return {
    select: selectTypes[name],
    filters: { ...filters, assetId: get(rowData, 'asset.id') },
  };
};

const allDependenciesHaveValues = props => {
  const { name, rowData } = props;
  const data = pick(rowData, getCellConfig(name, props).dependsOn);

  return Object.values(data).every(Boolean);
};

const allDependenciesHaveLoadedOptions = props => {
  const { name, rowData, tireOptionsByType: options } = props;
  const data = pick(rowData, getCellConfig(name, props).dependsOn);

  return Object.keys(data)
    .filter(key => areCellOptionsLoadedAsync(key, props))
    .every(key => get(options, `${get(rowData, 'id')}.${key}.length`));
};

export const shouldSkip = props =>
  !allDependenciesHaveValues(props) || !allDependenciesHaveLoadedOptions(props);

export const buildQueryChild = curry(
  (WrappedComponent, props, { data = {}, loading }) => {
    const { tiresOptions } = data;
    const { name, rowData, readOnly } = props;

    const opts = buildTireOptions(name, get(tiresOptions, 'options') || []);
    const isSeeNotes = get(rowData, name) === seeNotesRequestValue;

    const isLoading =
      isSeeNotes || (readOnly && allDependenciesHaveLoadedOptions(props))
        ? false
        : !!loading;

    return <WrappedComponent {...props} options={opts} isLoading={isLoading} />;
  },
);

const withTireOptions = WrappedComponent => componentProps => (
  <Query
    skip={shouldSkip(componentProps)}
    query={buildQueryForField(componentProps.name)}
    variables={buildVariables(componentProps)}
    fetchPolicy="cache-and-network"
  >
    {buildQueryChild(WrappedComponent, componentProps)}
  </Query>
);

export default withTireOptions;



// WEBPACK FOOTER //
// ./src/compositions/CaseRequestsPanel/CaseRequestLines/BaseRequestLinesTable/TireOptionsDropdownCell/withTireOptions.js